// ============================================================================
// GESTIMMONY — Page Tarifs publique (/tarifs)
// ============================================================================
// 3 offres : Essentiel / Patrimoine / Prestige — prix HT, mention « + TVA ».
// Toggle 2 états : Mensuel sans engagement / Engagement annuel (-20 %).
// Patrimoine mis en avant (badge « ⭐ Le plus choisi »).
// Détail des 20 modèles de courriers derrière un accordéon par offre.
// ============================================================================
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Check, Sparkles, ArrowRight, Loader2, ShieldCheck, ChevronDown, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogoMark } from "@/components/Logo";

// ─── 20 modèles de courriers — répartition par offre ─────────────────────
const LETTERS_ESSENTIEL = [
  "Relance amiable de loyer",
  "Accusé de réception du préavis locataire",
  "Renouvellement du bail",
  "Notification de révision IRL",
  "Attestation annuelle de loyers versés",
  "Attestation de résidence",
  "Changement d'IBAN bailleur",
  "Réponse à une demande de réparation",
];
const LETTERS_PATRIMOINE_EXTRA = [
  "Mise en demeure de payer",
  "Saisine du conciliateur de justice",
  "Congé pour vente",
  "Congé pour reprise",
  "Avenant au bail",
  "Régularisation annuelle des charges",
  "Convocation à l'état des lieux de sortie",
  "Restitution du dépôt de garantie",
];
const LETTERS_PRESTIGE_EXTRA = [
  "Retenue sur dépôt de garantie",
  "Récapitulatif fiscal annuel (déclaration 2044)",
  "Demande d'attestation d'assurance habitation",
  "Notification de travaux",
];

const PLANS = [
  {
    key: "essentiel",
    name: "Essentiel",
    tagline: "Pour s'organiser sans se prendre la tête",
    monthly: 15, annual: 144,
    prepaid_annual: 144,
    lookup: {
      monthly: "gestimmony_essentiel_monthly",
      annual: "gestimmony_essentiel_annual",
    },
    features: [
      "Jusqu'à 3 biens",
      "Quittances de loyer automatiques",
      "Suivi des paiements et impayés",
      "Tableau de bord locatif",
      "8 modèles de courriers pré-remplis",
      "Export PDF des documents",
      "Support par email",
    ],
    letters: LETTERS_ESSENTIEL,
    letters_locked: [...LETTERS_PATRIMOINE_EXTRA, ...LETTERS_PRESTIGE_EXTRA],
    highlighted: false,
    cta: "Commencer",
  },
  {
    key: "patrimoine",
    name: "Patrimoine",
    tagline: "Pour gérer sereinement les situations sensibles",
    monthly: 24, annual: 228,
    prepaid_annual: 228,
    lookup: {
      monthly: "gestimmony_patrimoine_monthly",
      annual: "gestimmony_patrimoine_annual",
    },
    features: [
      "Jusqu'à 10 biens",
      "Tout l'Essentiel +",
      "16 modèles de courriers (8 supplémentaires)",
      "Alertes échéances (préavis, révision, régul')",
      "Portail locataire (quittances, incidents)",
      "Révision IRL automatique",
      "Support prioritaire",
    ],
    letters: [...LETTERS_ESSENTIEL, ...LETTERS_PATRIMOINE_EXTRA],
    letters_locked: LETTERS_PRESTIGE_EXTRA,
    highlighted: true,
    badge: "⭐ Le plus choisi",
    cta: "Choisir Patrimoine",
  },
  {
    key: "prestige",
    name: "Prestige",
    tagline: "Pour le multi-propriétaire et l'investisseur exigeant",
    monthly: 44, annual: 420,
    prepaid_annual: 420,
    lookup: {
      monthly: "gestimmony_prestige_monthly",
      annual: "gestimmony_prestige_annual",
    },
    features: [
      "Jusqu'à 20 biens (sur devis au-delà)",
      "Tout le Patrimoine +",
      "Les 20 modèles de courriers",
      "Support prioritaire",
    ],
    letters: [...LETTERS_ESSENTIEL, ...LETTERS_PATRIMOINE_EXTRA, ...LETTERS_PRESTIGE_EXTRA],
    letters_locked: [],
    highlighted: false,
    cta: "Choisir Prestige",
  },
];

const BILLING_LABEL = {
  monthly: "Mensuel",
  annual: "Annuel",
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const [openLetters, setOpenLetters] = useState(null); // plan.key or null

  const subscribe = async (plan) => {
    const lookup = plan.lookup[billing];
    if (!user) {
      sessionStorage.setItem("gestimmony_pending_plan", lookup);
      navigate(`/register?plan=${plan.key}`);
      return;
    }
    setBusy(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
      setBusy(null);
    }
  };

  const priceOf = (plan) => {
    if (billing === "monthly") return plan.monthly;
    return Math.round(plan.annual / 12 * 10) / 10; // équivalent mensuel annuel
  };

  const subtextOf = (plan) => {
    if (billing === "monthly") return "facturé mensuellement, sans engagement";
    return `facturé mensuellement · engagement 12 mois (−20 %)`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/40 to-white">
      <Seo
        title="Tarifs — Gestimmony, logiciel de gestion locative dès 12 €/mois"
        description="Trois formules (Essentiel, Patrimoine, Prestige) de 12 à 44 €/mois. Mensuel sans engagement ou annuel −20 % avec 14 jours d'essai gratuit. Paiement sécurisé."
        path="/tarifs"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Gestimmony — Logiciel de gestion locative",
          description: "Gestion locative complète pour propriétaires bailleurs : quittances, baux, états des lieux, finances et déclaration 2044.",
          brand: { "@type": "Brand", name: "Gestimmony" },
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "12",
            highPrice: "44",
            offerCount: "3",
          },
        }}
      />
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="pricing-home-link">
          <LogoMark className="h-9 shrink-0" />
          <span className="font-heading font-bold text-lg tracking-tight truncate">Gestimmony</span>
        </Link>
        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <Link to="/app"><Button size="sm" variant="ghost" data-testid="pricing-app-btn">Mon espace</Button></Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block"><Button size="sm" variant="ghost" data-testid="pricing-login-btn">Se connecter</Button></Link>
              <Link to="/register"><Button size="sm" data-testid="pricing-register-btn">Créer un compte</Button></Link>
            </>
          )}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-8 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-teal-100 text-teal-800 px-3 py-1 text-xs font-semibold mb-4">
          <Sparkles className="h-3.5 w-3.5" /> Sans engagement en mensuel
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
          3 formules pour votre parc locatif
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
          Choisissez l'offre qui vous ressemble. En mensuel : sans engagement, résiliable
          à tout moment. En annuel (−20 %) : facturé mensuellement avec engagement 12 mois et 14 jours d'essai gratuit.
        </p>

        {/* Toggle 3 états */}
        <div
          className="mt-6 inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 p-1 shadow-sm flex-wrap"
          data-testid="pricing-billing-toggle"
        >
          {[
            { k: "monthly",    label: "Mensuel",        badge: null },
            { k: "annual",     label: "Annuel",         badge: "−20 %" },
          ].map(({ k, label, badge }) => (
            <button
              key={k}
              onClick={() => setBilling(k)}
              data-testid={`pricing-toggle-${k}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                billing === k ? "bg-teal-700 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
              {badge && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  billing === k ? "bg-amber-400 text-slate-900" : "bg-amber-100 text-amber-800"
                }`}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Grille des 3 offres */}
      <section className="max-w-6xl mx-auto px-6 pb-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            data-testid={`pricing-plan-${plan.key}`}
            className={`relative rounded-2xl border p-6 flex flex-col ${
              plan.highlighted
                ? "border-teal-500 shadow-lg ring-2 ring-teal-500/20 bg-white scale-[1.02]"
                : "border-slate-200 bg-white/80"
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                {plan.badge}
              </div>
            )}
            <h3 className="font-heading font-bold text-xl text-slate-900">{plan.name}</h3>
            <p className="text-sm text-slate-500 mt-1 min-h-[2.5rem]">{plan.tagline}</p>

            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-bold text-slate-900" data-testid={`pricing-amount-${plan.key}`}>
                {String(priceOf(plan)).replace(".", ",")}
              </span>
              <span className="text-lg text-slate-500">€</span>
              <span className="text-sm text-slate-500">HT / mois</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">+ TVA selon territoire (voir détail en bas de page)</p>
            <p className="text-xs text-slate-500 mt-1 h-5">{subtextOf(plan)}</p>
            <p className="text-[11px] text-emerald-700 font-semibold mt-1">
              {billing === "monthly"
                ? "Sans engagement — résiliable à tout moment"
                : "Engagement 12 mois — résiliation en fin de période"}
            </p>

            <ul className="mt-5 space-y-2.5 flex-1">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Accordéon « Voir les modèles inclus » */}
            <button
              type="button"
              onClick={() => setOpenLetters(openLetters === plan.key ? null : plan.key)}
              data-testid={`pricing-letters-toggle-${plan.key}`}
              className="mt-5 flex items-center justify-between text-xs font-semibold text-teal-700 hover:text-teal-800 transition-colors"
            >
              <span>Voir les modèles inclus ({plan.letters.length}/20)</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${openLetters === plan.key ? "rotate-180" : ""}`} />
            </button>
            {openLetters === plan.key && (
              <div
                className="mt-3 space-y-1.5 text-xs text-slate-600"
                data-testid={`pricing-letters-list-${plan.key}`}
              >
                {plan.letters.map((l, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <Check className="h-3 w-3 text-teal-600 mt-0.5 shrink-0" />
                    <span>{l}</span>
                  </div>
                ))}
                {plan.letters_locked.map((l, i) => (
                  <div key={`l${i}`} className="flex items-start gap-1.5 text-slate-400">
                    <Lock className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{l}</span>
                  </div>
                ))}
              </div>
            )}

            <Button
              className="mt-6 w-full"
              variant={plan.highlighted ? "default" : "outline"}
              data-testid={`pricing-cta-${plan.key}`}
              disabled={busy === plan.key}
              onClick={() => subscribe(plan)}
            >
              {busy === plan.key ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection…</>
              ) : (
                <>{plan.cta} <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </div>
        ))}
      </section>

      {/* Réassurance RGPD / confidentialité */}
      <section className="max-w-6xl mx-auto px-6 pb-6">
        <div
          className="rounded-xl border border-teal-200 bg-teal-50 px-5 py-4 flex items-center justify-center gap-3 text-center"
          data-testid="pricing-privacy-badge"
        >
          <Lock className="h-5 w-5 text-teal-700 shrink-0" />
          <p className="text-sm text-teal-900">
            <b>Vos données restent privées</b> — Isolation stricte, exports garantis, conforme RGPD.
          </p>
        </div>
      </section>

      {/* Badge rassurance */}
      <section className="max-w-5xl mx-auto px-6 pb-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-start gap-3" data-testid="pricing-reassurance-badge">
          <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-900">
            <b>20 modèles rédigés selon la législation en vigueur</b> et mis à jour automatiquement
            à chaque évolution réglementaire (loi ALUR, loi Climat, décret encadrement des loyers, IRL INSEE…).
          </p>
        </div>
      </section>

      {/* Ancre de prix */}
      <section className="max-w-4xl mx-auto px-6 pb-14 text-center" data-testid="pricing-anchor-value">
        <div className="inline-block rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <p className="text-sm text-slate-700 leading-relaxed">
            💬 Une seule mise en demeure rédigée par un professionnel coûte entre
            <b className="text-slate-900"> 80 € et 150 €</b>.
            <br />
            Gestimmony vous la génère en <b className="text-slate-900">2 minutes</b>,
            conforme et pré-remplie à partir du bail.
          </p>
        </div>
      </section>

      {/* Mentions légales de la grille */}
      <section className="max-w-4xl mx-auto px-6 pb-16 text-center text-xs text-slate-500 space-y-2">
        <p>
          Tous les prix sont affichés en euros <b>hors taxes</b>. La TVA applicable à votre
          territoire de facturation est ajoutée automatiquement sur la facture :
          <b> 20 % en métropole</b>, <b>8,5 % en Guadeloupe, Martinique et La Réunion</b>,
          pas de TVA en Guyane et à Mayotte. Les autres collectivités d'outre-mer relèvent
          de régimes locaux spécifiques (détail en facture).
          En formule mensuelle, le premier prélèvement intervient immédiatement à la
          souscription. En formule annuelle, vous bénéficiez de 14 jours d'essai gratuit :
          le premier prélèvement n'intervient qu'à l'issue de cette période d'essai.
        </p>
        <p>
          La formule annuelle est souscrite avec un engagement de 12 mois : la résiliation
          prend effet à la fin de la période d'engagement en cours (voir CGU).
        </p>
        <p>
          Au-delà de 20 lots, <Link to="/login" className="text-teal-700 font-semibold hover:underline">contactez-nous</Link> pour un devis sur-mesure.
        </p>
      </section>
    </div>
  );
}
